import { Injectable, signal, inject } from '@angular/core';
import { HttpService } from './core/http.service';
import { UserDataService } from './core/user-data.service';
import { AuthSessionService } from './core/auth-session.service';
import { IS_OPERATOR_OR_SUPPORT_P2_KEY } from '../shared/constants/storage-keys.constants';
import { TranslateService } from '@ngx-translate/core';
import { Router } from '@angular/router';
import { ICredentialsLoginResponse } from '../shared/interfaces/ICredentialsLoginResponse';
import { OnboardingService2 } from './onboarding.service2';
import { LoggingService } from './core/logging.service';

@Injectable({
  providedIn: 'root',
})
export class LoginService {
  private http = inject(HttpService);
  private user = inject(UserDataService);
  private authSession = inject(AuthSessionService);
  private translate = inject(TranslateService);
  private router = inject(Router);
  private guidedTourService = inject(OnboardingService2);
  private logging = inject(LoggingService);

  public mfaExpired = signal<boolean | undefined>(undefined);
  public wrongMfa = signal<boolean | undefined>(undefined);

  public isOperatorOrSupportP2 = signal(false);

  setMfaExpired(mfa_expired: boolean) {
    this.mfaExpired.set(mfa_expired);
  }

  setWrongMfa(wrong_mfa: boolean) {
    this.wrongMfa.set(wrong_mfa);
  }

  setIsOperatorOrSupportP2(value: boolean) {
    this.isOperatorOrSupportP2.set(value);
    localStorage.setItem(IS_OPERATOR_OR_SUPPORT_P2_KEY, JSON.stringify(value));
  }

  async credentialsLogin(username, userpass, rememberMe): Promise<ICredentialsLoginResponse> {
    const user_info = {
      username: username,
      userpass: userpass,
      extended: rememberMe,
    };

    this.logging.logInfo('-- pweb_login❓', user_info.username);

    try {
      const loginResponse = await this.http.maestroUnauthPost('v2/pweb_login', user_info);

      if (loginResponse == null) {
        this.signOut();
        return { success: false, isMfaRequired: false, error: 'Credenciais inválidas' };
      }

      if (loginResponse['mfa'] === false) {
        return { success: true, isMfaRequired: false, jwt: loginResponse.jwt };
      }

      if (loginResponse.mfa === true) {
        return { success: true, isMfaRequired: true, jwt: loginResponse.jwt };
      }

      return {
        success: false,
        isMfaRequired: false,
        error: 'Erro desconhecido. Por favor, contate o suporte',
      };
    } catch (error) {
      this.logging.logError('pweb_login', error);
      this.signOut();

      if (error.status === 401 || error.status === 403) {
        return { success: false, isMfaRequired: false, error: 'Credenciais inválidas' };
      }

      return {
        success: false,
        isMfaRequired: false,
        error: 'Falha na rede ou comunicação com o servidor',
      };
    }
  }

  async mfaLogin(username, userpass, rememberMe, mfaCode) {
    const mfaData = {
      username: username.trim(),
      userpass: userpass.trim(),
      extended: rememberMe,
      mfa_code: mfaCode.trim(),
    };

    const mfaResponse = await this.http.maestroUnauthPost('v2/pweb_login', mfaData);

    if (mfaResponse == null) {
      this.signOut();
      return false;
    }

    if (mfaResponse['mfa'] === false && mfaResponse['jwt']) {
      await this.tokenLogin(mfaResponse['jwt']);
      return true;
    } else {
      const errorMessage = mfaResponse.error;
      this.setMfaExpired(errorMessage === 'Código expirado');
      this.setWrongMfa(errorMessage === 'Código inválido');
      return false;
    }
  }

  async tokenLogin(userToken) {
    this.authSession.saveToken(userToken);
    this.user.saveLang(this.translate.currentLang);

    const tokens = await this.http.maestroUserGet('v2/get_maestro_tokens');
    this.authSession.saveSocketCentralToken(tokens['socket']);
    this.authSession.saveBackendEdgeToken(tokens['p2_back']);

    const isP2 = JSON.parse(localStorage.getItem('IS_OPERATOR_OR_SUPPORT_P2'));
    if (!isP2) {
      this.router.navigateByUrl('/map');
    } else {
      this.router.navigateByUrl('/interface');
    }

    this.logging.logInfo('-- ✔️Logged in: user validated');
  }

  signOut() {
    this.user.signOut();
  }
}
